import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Toast } from '@/components/ui/Toast'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { SkeletonPosMakeSales } from '@/components/ui/Skeleton'
import { fetchProducts } from '@/services/productService'
import * as categoryService from '@/services/categoryService'
import { useAuth } from '@/context/AuthContext'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'
import { DEFAULT_LOW_STOCK_THRESHOLD, normalizeLowStockThreshold } from '@/lib/inventoryStock'
import * as posApi from './posApi'
import { PosReceiptModal, type PosReceiptPayload } from './PosReceipt'
import { PosCartDrawer } from './PosCartDrawer'
import { PosCartContent } from './PosCartContent'
import { PosCartPanel } from './PosCartPanel'
import { PosCatalogPanel, type PosCategoryChip } from './PosCatalogPanel'
import { PosMobileCartBar } from './PosMobileCartBar'
import { PosSyncQueueDrawer } from './PosSyncQueueDrawer'
import {
  deletePendingSale,
  getCatalogCache,
  listPendingSalesForStore,
  putCatalogCache,
  putPendingSale,
} from './offline/db'
import { LS_LAST_SYNC_ATTEMPT, POS_CATALOG_STALE_MS } from './offline/constants'
import { parsePosSaleError, plainLanguageForPosError } from './offline/errorMap'
import type { CachedPosProduct, PendingPosSale } from './offline/types'

type SaleStep = 'edit' | 'review'

type CartLine = {
  productId: number
  name: string
  price: number
  quantity: number
}

function normalizePosProduct(raw: Record<string, unknown>): CachedPosProduct | null {
  const id = Number(raw.id)
  if (!Number.isFinite(id)) return null
  const name = typeof raw.name === 'string' ? raw.name : ''
  if (!name) return null
  const images = Array.isArray(raw.images)
    ? raw.images.filter((u): u is string => typeof u === 'string' && u.length > 0)
    : []
  const stockNum =
    raw.stockQuantity == null || raw.stockQuantity === '' ? NaN : Number(raw.stockQuantity)
  const storeIdNum = raw.storeId == null || raw.storeId === '' ? null : Number(raw.storeId)
  return {
    id,
    name,
    price: raw.price as string | number,
    storeId: storeIdNum != null && Number.isFinite(storeIdNum) ? storeIdNum : null,
    isActive: raw.isActive === undefined ? undefined : Boolean(raw.isActive),
    stockQuantity: Number.isFinite(stockNum) ? stockNum : undefined,
    images,
    sku: typeof raw.sku === 'string' && raw.sku.trim() ? raw.sku : null,
    categorySlug:
      typeof raw.categorySlug === 'string' && raw.categorySlug.trim() ? raw.categorySlug : null,
  }
}

function readLastSyncAttempt(): string | null {
  try {
    return localStorage.getItem(LS_LAST_SYNC_ATTEMPT)
  } catch {
    return null
  }
}

function writeLastSyncAttempt(iso: string) {
  try {
    localStorage.setItem(LS_LAST_SYNC_ATTEMPT, iso)
  } catch {
    /* ignore */
  }
}

export default function MakeSalesPage() {
  const { user } = useAuth()
  const { currentStoreId: storeId, storeRecord, loading: storeContextLoading } = useCurrentStore()
  const posEnabled = Boolean(storeRecord?.posEnabled)
  const [products, setProducts] = useState<CachedPosProduct[]>([])
  const [categoryChips, setCategoryChips] = useState<PosCategoryChip[]>([])
  const [loading, setLoading] = useState(true)
  const [productsLoading, setProductsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [categorySlug, setCategorySlug] = useState<string | null>(null)
  const [cart, setCart] = useState<Record<number, CartLine>>({})
  const [paymentMethod, setPaymentMethod] = useState<posApi.PosPaymentMethod>('cash')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<PosReceiptPayload | null>(null)
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' | 'info' } | null>(
    null,
  )

  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )
  const [catalogUpdatedAt, setCatalogUpdatedAt] = useState<number | null>(null)
  const [pendingSales, setPendingSales] = useState<PendingPosSale[]>([])
  const [queueOpen, setQueueOpen] = useState(false)
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false)
  const [saleStep, setSaleStep] = useState<SaleStep>('edit')
  const cartDrawerBodyRef = useRef<HTMLDivElement>(null)
  const [syncInProgress, setSyncInProgress] = useState(false)
  const [lastSyncAttemptIso, setLastSyncAttemptIso] = useState<string | null>(() => readLastSyncAttempt())
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>(null)
  const [addedHighlightProductId, setAddedHighlightProductId] = useState<number | null>(null)

  const lowStockThreshold = normalizeLowStockThreshold(
    storeRecord?.lowStockThreshold ?? DEFAULT_LOW_STOCK_THRESHOLD,
  )

  useEffect(() => {
    if (storeContextLoading) return
    if (!posEnabled || storeId == null) {
      setLoading(false)
      return
    }
    setLoading(false)
  }, [storeContextLoading, posEnabled, storeId])

  const refreshQueue = useCallback(async (sid: number | null) => {
    if (sid == null) {
      setPendingSales([])
      return
    }
    const rows = await listPendingSalesForStore(sid)
    rows.sort((a, b) => a.createdAt - b.createdAt)
    setPendingSales(rows)
  }, [])

  useEffect(() => {
    void refreshQueue(storeId)
  }, [storeId, refreshQueue])

  useEffect(() => {
    function onOnline() {
      setIsOnline(true)
    }
    function onOffline() {
      setIsOnline(false)
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  const loadCategories = useCallback(async () => {
    if (storeId == null || !navigator.onLine) return
    try {
      const res = await categoryService.fetchCategories({ page: 1, limit: 100 })
      const storeCats = (res.items ?? []).filter((c) => c.source === 'store' && c.slug)
      setCategoryChips(storeCats.map((c) => ({ slug: c.slug, name: c.name })))
    } catch {
      /* keep last chips; offline filter still works from product categorySlug */
    }
  }, [storeId])

  const loadProducts = useCallback(async () => {
    if (storeId == null) return
    setProductsLoading(true)
    setError(null)
    try {
      const cached = await getCatalogCache(storeId)
      if (cached?.products?.length) {
        setCatalogUpdatedAt(cached.updatedAt)
        setProducts(cached.products.filter((p) => p.storeId === storeId || p.storeId == null))
      }

      if (!navigator.onLine) {
        if (!cached?.products?.length) {
          setProducts([])
          setError('Connect to the internet to load the product catalog.')
        }
        setProductsLoading(false)
        return
      }

      const res = await fetchProducts({ page: 1, limit: 100, isActive: true })
      const rows = (res.items ?? [])
        .map((item) => normalizePosProduct(item as Record<string, unknown>))
        .filter((p): p is CachedPosProduct => p != null && p.storeId === storeId)
      const now = Date.now()
      await putCatalogCache({ storeId, updatedAt: now, products: rows })
      setCatalogUpdatedAt(now)
      setProducts(rows)
      void loadCategories()
    } catch (e) {
      if (!navigator.onLine) {
        const cached = await getCatalogCache(storeId)
        if (cached?.products?.length) {
          setCatalogUpdatedAt(cached.updatedAt)
          setProducts(cached.products.filter((p) => p.storeId === storeId || p.storeId == null))
        }
        setError('You are offline. Showing the last saved catalog.')
      } else {
        setError(getUserFacingErrorMessage(e, 'Failed to load products'))
      }
    } finally {
      setProductsLoading(false)
    }
  }, [storeId, loadCategories])

  useEffect(() => {
    void loadProducts()
  }, [loadProducts])

  useEffect(() => {
    void loadCategories()
  }, [loadCategories])

  const drainQueue = useCallback(async () => {
    if (storeId == null || !navigator.onLine) return
    const rows = await listPendingSalesForStore(storeId)
    const pending = rows
      .filter((r) => r.status === 'pending' || r.status === 'syncing')
      .sort((a, b) => a.createdAt - b.createdAt)
    if (pending.length === 0) return

    setSyncInProgress(true)
    for (const row of pending) {
      const attemptIso = new Date().toISOString()
      writeLastSyncAttempt(attemptIso)
      setLastSyncAttemptIso(attemptIso)

      await putPendingSale({ ...row, status: 'syncing' })
      await refreshQueue(storeId)

      try {
        await posApi.createPosSale(storeId, {
          items: row.payload.items,
          paymentMethod: row.payload.paymentMethod,
          idempotencyKey: row.idempotencyKey,
          recordedAtDevice: row.recordedAtDevice,
        })
        await deletePendingSale(row.localId)
      } catch (e) {
        const p = parsePosSaleError(e)
        const msg = plainLanguageForPosError(p.code, p.message)
        if (p.status === 409 && p.code === 'INSUFFICIENT_STOCK_ON_SYNC') {
          const raw = p.details as
            | { lines?: Array<{ productId: number; requestedQuantity: number; availableQuantity: number }> }
            | undefined
          const lines = raw?.lines
          await putPendingSale({
            ...row,
            status: 'failed',
            lastError: msg,
            errorCode: p.code,
            conflictDetails: lines ? { lines } : undefined,
          })
        } else if (p.status === 409 && p.code === 'STOCK_CONFLICT') {
          await putPendingSale({
            ...row,
            status: 'failed',
            lastError: msg,
            errorCode: p.code,
          })
        } else if (
          !navigator.onLine ||
          p.status == null ||
          p.status >= 500 ||
          p.message.toLowerCase().includes('could not reach')
        ) {
          await putPendingSale({
            ...row,
            status: 'pending',
            lastError: 'Offline or network error — will retry when online.',
            errorCode: 'NETWORK',
          })
        } else {
          await putPendingSale({
            ...row,
            status: 'failed',
            lastError: msg,
            errorCode: p.code ?? 'UNKNOWN',
          })
        }
      }
      await refreshQueue(storeId)
    }
    setSyncInProgress(false)
  }, [storeId, refreshQueue])

  useEffect(() => {
    if (!isOnline || storeId == null) return
    const t = window.setInterval(() => {
      void drainQueue()
    }, 25000)
    return () => window.clearInterval(t)
  }, [isOnline, storeId, drainQueue])

  useEffect(() => {
    if (!isOnline || storeId == null) return
    void drainQueue()
  }, [isOnline, storeId, drainQueue])

  useEffect(() => {
    function onFocus() {
      if (navigator.onLine && storeId != null) void drainQueue()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [storeId, drainQueue])

  const visibleCategoryChips = useMemo(() => {
    const slugsInCatalog = new Set(
      products.map((p) => p.categorySlug).filter((s): s is string => Boolean(s)),
    )
    if (categoryChips.length > 0) {
      return categoryChips.filter((c) => slugsInCatalog.has(c.slug))
    }
    return [...slugsInCatalog].map((slug) => ({ slug, name: slug }))
  }, [categoryChips, products])

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter((p) => {
      if (categorySlug && p.categorySlug !== categorySlug) return false
      if (!q) return true
      const nameHit = p.name.toLowerCase().includes(q)
      const skuHit = (p.sku ?? '').toLowerCase().includes(q)
      return nameHit || skuHit
    })
  }, [products, search, categorySlug])

  const storeRow = storeRecord
  const cartLines = useMemo(() => Object.values(cart), [cart])

  const prevCartDrawerOpen = useRef(cartDrawerOpen)
  useEffect(() => {
    if (prevCartDrawerOpen.current && !cartDrawerOpen) {
      setSaleStep('edit')
    }
    prevCartDrawerOpen.current = cartDrawerOpen
  }, [cartDrawerOpen])

  useEffect(() => {
    if (saleStep === 'review' && cartLines.length === 0) setSaleStep('edit')
  }, [saleStep, cartLines.length])

  useEffect(() => {
    if (saleStep === 'review' && cartDrawerOpen) {
      cartDrawerBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [saleStep, cartDrawerOpen])

  const { subtotal, taxTotal, grandTotal } = useMemo(() => {
    const taxRate = Number(storeRow?.taxRatePercent ?? 0)
    const sub = cartLines.reduce((s, l) => s + l.price * l.quantity, 0)
    const tax = sub * (Math.min(100, Math.max(0, taxRate)) / 100)
    const total = sub + tax
    return {
      subtotal: sub,
      taxTotal: tax,
      grandTotal: total,
    }
  }, [cartLines, storeRow?.taxRatePercent])

  const catalogStale =
    catalogUpdatedAt != null && Date.now() - catalogUpdatedAt > POS_CATALOG_STALE_MS && isOnline

  const queueCount = pendingSales.filter(
    (p) => p.status === 'pending' || p.status === 'failed' || p.status === 'syncing',
  ).length

  const cartUnitCount = useMemo(() => cartLines.reduce((sum, l) => sum + l.quantity, 0), [cartLines])

  const cartQuantities = useMemo(() => {
    const map: Record<number, number> = {}
    for (const l of cartLines) map[l.productId] = l.quantity
    return map
  }, [cartLines])

  function estimateTotalLabel(
    items: Array<{ productId: number; quantity: number }>,
    taxRatePercent: number,
  ): string {
    let sub = 0
    for (const it of items) {
      const pr = products.find((x) => x.id === it.productId)
      if (pr) sub += Number(pr.price) * it.quantity
    }
    const tr = Math.min(100, Math.max(0, taxRatePercent))
    const tax = sub * (tr / 100)
    return (sub + tax).toFixed(2)
  }

  function addToCart(p: CachedPosProduct) {
    const price = Number(p.price)
    if (!Number.isFinite(price)) return
    let didAdd = false
    setCart((prev) => {
      const cur = prev[p.id]
      const nextQty = (cur?.quantity ?? 0) + 1
      const max = p.stockQuantity
      if (max != null && Number.isFinite(Number(max)) && nextQty > Number(max)) {
        return prev
      }
      didAdd = true
      return {
        ...prev,
        [p.id]: {
          productId: p.id,
          name: p.name,
          price,
          quantity: nextQty,
        },
      }
    })
    if (didAdd) {
      setAddedHighlightProductId(p.id)
      window.setTimeout(() => {
        setAddedHighlightProductId((id) => (id === p.id ? null : id))
      }, 900)
    } else if (p.stockQuantity != null && Number.isFinite(Number(p.stockQuantity))) {
      setToast({
        message: `Only ${Number(p.stockQuantity)} available for ${p.name}.`,
        variant: 'error',
      })
    }
  }

  function adjustLineQty(productId: number, delta: number) {
    setCart((prev) => {
      const line = prev[productId]
      if (!line) return prev
      const next = line.quantity + delta
      if (next < 1) {
        const out = { ...prev }
        delete out[productId]
        return out
      }
      if (next > 9999) return prev
      if (delta > 0) {
        const product = products.find((p) => p.id === productId)
        const max = product?.stockQuantity
        if (max != null && Number.isFinite(Number(max)) && next > Number(max)) {
          return prev
        }
      }
      return { ...prev, [productId]: { ...line, quantity: next } }
    })
  }

  function clearCart() {
    setCart({})
    setSaleStep('edit')
  }

  async function completeSale() {
    if (storeId == null || cartLines.length === 0) return
    const items = cartLines.map((l) => ({ productId: l.productId, quantity: l.quantity }))
    const idempotencyKey = crypto.randomUUID()
    const recordedAt = new Date().toISOString()

    setSubmitting(true)
    setError(null)

    if (!navigator.onLine) {
      const totalLabel = grandTotal.toFixed(2)
      const row: PendingPosSale = {
        localId: crypto.randomUUID(),
        idempotencyKey,
        storeId,
        payload: { items, paymentMethod },
        lineLabels: cartLines.map((l) => ({ productId: l.productId, name: l.name })),
        totalLabel,
        createdAt: Date.now(),
        recordedAtDevice: recordedAt,
        status: 'pending',
      }
      await putPendingSale(row)
      await refreshQueue(storeId)
      const staffName = user ? user.name || user.email || undefined : undefined
      setCartDrawerOpen(false)
      setSaleStep('edit')
      setReceipt({
        storeName: storeRow?.name ?? 'Store',
        saleReference: 'Pending sync',
        createdAt: recordedAt,
        currency: storeRow?.currency ?? 'USD',
        paymentMethod,
        subtotal: subtotal.toFixed(2),
        taxTotal: taxTotal.toFixed(2),
        total: totalLabel,
        staffName,
        lines: cartLines.map((l) => ({
          name: l.name,
          quantity: l.quantity,
          lineTotal: String((l.price * l.quantity).toFixed(2)),
        })),
        pendingSync: true,
        localRefShort: idempotencyKey.replace(/-/g, '').slice(-6).toUpperCase(),
      })
      setCart({})
      setToast({ message: 'Sale saved — will sync when online', variant: 'success' })
      setSubmitting(false)
      return
    }

    try {
      const res = await posApi.createPosSale(storeId, {
        items,
        paymentMethod,
        idempotencyKey,
        recordedAtDevice: recordedAt,
      })
      const staffName = user ? user.name || user.email || undefined : undefined
      setCartDrawerOpen(false)
      setSaleStep('edit')
      setReceipt({
        storeName: storeRow?.name ?? 'Store',
        saleReference: res.saleReference,
        createdAt: res.createdAt,
        currency: res.currency,
        paymentMethod: res.paymentMethod,
        subtotal: res.subtotal,
        taxTotal: res.taxTotal,
        total: res.total,
        staffName,
        lines: cartLines.map((l) => ({
          name: l.name,
          quantity: l.quantity,
          lineTotal: String((l.price * l.quantity).toFixed(2)),
        })),
        replayed: res.replayed,
      })
      setCart({})
      if (res.replayed) {
        setToast({ message: 'Sale was already recorded (synced earlier).', variant: 'info' })
      }
      void drainQueue()
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not complete sale'))
      setToast({
        message: getUserFacingErrorMessage(e, 'Could not complete sale'),
        variant: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRefreshCatalog() {
    if (!navigator.onLine) {
      setToast({ message: 'Connect to the internet to refresh the catalog.', variant: 'error' })
      return
    }
    setProductsLoading(true)
    try {
      await loadProducts()
      setToast({ message: 'Catalog updated', variant: 'success' })
    } catch {
      setToast({ message: 'Could not refresh catalog', variant: 'error' })
    }
  }

  async function handleRetry(localId: string) {
    const row = pendingSales.find((p) => p.localId === localId)
    if (!row) return
    await putPendingSale({
      ...row,
      status: 'pending',
      lastError: undefined,
      errorCode: undefined,
      conflictDetails: undefined,
    })
    await refreshQueue(storeId!)
    void drainQueue()
  }

  async function handleRetryAllFailed() {
    const failed = pendingSales.filter((p) => p.status === 'failed')
    for (const row of failed) {
      await putPendingSale({
        ...row,
        status: 'pending',
        lastError: undefined,
        errorCode: undefined,
        conflictDetails: undefined,
      })
    }
    await refreshQueue(storeId!)
    void drainQueue()
  }

  async function handleRemoveConfirmed() {
    if (removeConfirmId == null || storeId == null) return
    await deletePendingSale(removeConfirmId)
    setRemoveConfirmId(null)
    await refreshQueue(storeId)
  }

  async function handleApplyStockFix(localId: string) {
    const row = pendingSales.find((p) => p.localId === localId)
    if (!row?.conflictDetails?.lines?.length) return
    const cap = new Map(row.conflictDetails.lines.map((l) => [l.productId, l.availableQuantity]))
    const nextItems = row.payload.items
      .map((it) => ({
        ...it,
        quantity: Math.min(it.quantity, cap.get(it.productId) ?? it.quantity),
      }))
      .filter((it) => it.quantity > 0)
    if (nextItems.length === 0) {
      setToast({ message: 'Nothing left to sell — remove this sale from the queue.', variant: 'error' })
      return
    }
    const ids = new Set(nextItems.map((i) => i.productId))
    const lineLabels = row.lineLabels.filter((l) => ids.has(l.productId))
    const totalLabel = estimateTotalLabel(nextItems, Number(storeRow?.taxRatePercent ?? 0))
    await putPendingSale({
      ...row,
      payload: { ...row.payload, items: nextItems },
      lineLabels,
      totalLabel,
      status: 'pending',
      lastError: undefined,
      errorCode: undefined,
      conflictDetails: undefined,
    })
    await refreshQueue(storeId!)
    void drainQueue()
    setToast({ message: 'Quantities updated — syncing…', variant: 'info' })
  }

  function handlePrint() {
    window.print()
  }

  if (loading) {
    return <SkeletonPosMakeSales />
  }

  if (!posEnabled || storeId == null) {
    return (
      <Card>
        <CardHeader
          title="POS unavailable"
          description="In-store POS is not enabled for your store. Contact Kartly support to turn it on."
        />
      </Card>
    )
  }

  const currency = storeRow?.currency ?? 'USD'

  const cartContentProps = {
    saleStep,
    cartLines,
    currency,
    storeName: storeRow?.name,
    subtotal,
    taxTotal,
    grandTotal,
    paymentMethod,
    submitting,
    storeId,
    onPaymentMethodChange: setPaymentMethod,
    onAdjustQty: adjustLineQty,
    onClearCart: clearCart,
    onCheckout: () => setSaleStep('review'),
    onBackToEdit: () => setSaleStep('edit'),
    onFinalize: () => void completeSale(),
  }

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      {toast ? (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      ) : null}
      <ConfirmModal
        open={removeConfirmId != null}
        title="Remove queued sale?"
        description="This permanently removes the sale from this device. It will not be sent to the server."
        confirmLabel="Remove"
        variant="danger"
        onClose={() => setRemoveConfirmId(null)}
        onConfirm={handleRemoveConfirmed}
      />
      <PosSyncQueueDrawer
        open={queueOpen}
        onOpenChange={setQueueOpen}
        items={pendingSales}
        isOnline={isOnline}
        syncInProgress={syncInProgress}
        lastSyncAttemptIso={lastSyncAttemptIso}
        onRetry={handleRetry}
        onRetryAllFailed={handleRetryAllFailed}
        onRemove={(id) => setRemoveConfirmId(id)}
        onApplyStockFix={handleApplyStockFix}
      />

      {receipt ? (
        <PosReceiptModal data={receipt} onPrint={handlePrint} onClose={() => setReceipt(null)} />
      ) : null}

      <div className="flex min-h-0 flex-1 gap-4">
        <PosCatalogPanel
          search={search}
          onSearchChange={setSearch}
          categorySlug={categorySlug}
          onCategoryChange={setCategorySlug}
          categories={visibleCategoryChips}
          products={filteredProducts}
          cartQuantities={cartQuantities}
          currency={currency}
          lowStockThreshold={lowStockThreshold}
          addedHighlightProductId={addedHighlightProductId}
          productsLoading={productsLoading}
          error={error}
          catalogUpdatedAt={catalogUpdatedAt}
          catalogStale={catalogStale}
          isOnline={isOnline}
          queueCount={queueCount}
          onRefreshCatalog={() => void handleRefreshCatalog()}
          onOpenQueue={() => setQueueOpen(true)}
          onAddProduct={addToCart}
        />

        <PosCartPanel {...cartContentProps} />
      </div>

      <PosMobileCartBar
        unitCount={cartUnitCount}
        total={grandTotal}
        currency={currency}
        onOpen={() => setCartDrawerOpen(true)}
      />

      <div className="lg:hidden">
        <PosCartDrawer
          open={cartDrawerOpen}
          onOpenChange={setCartDrawerOpen}
          title={saleStep === 'review' ? 'Confirm this sale' : 'Current sale'}
          description={
            saleStep === 'review'
              ? 'Check totals and payment before recording.'
              : 'Review lines, adjust quantities, then take payment.'
          }
          bodyRef={cartDrawerBodyRef}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <PosCartContent {...cartContentProps} />
          </div>
        </PosCartDrawer>
      </div>
    </div>
  )
}
